import { useBooking } from "@/store/useBooking";
import { useCalendarStore } from "@/store/useCalendarStore";
import { getBookingStatus } from "@/utils/Formatting";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { useLocationStore } from "@/utils/LocationStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import isEmpty from "lodash/isEmpty";
import { defineStore } from "pinia";
import { ref } from 'vue';
import { useRoute, useRouter } from "vue-router";

export const bookingGlobalPopupStore = defineStore("bookingGlobalPopupStore", () => {
  // Data
  const showTermsConditionsDialog = ref(false)
  const bookingToAgreeTermsFor = ref<any>(null)

  const isEditing = ref(false)
  const route = useRoute()

  const router = useRouter()
  const calendar = useCalendarStore();
  const booking = useBooking();
  const location = useLocationStore();
  const userStore = useUserStore();
  const userRole = useUserRole();
  const globalStore = useGlobalSetupStore();
  const bookingLoading = ref(false)

  const { getAllAreas, getActivityList } = booking;
  const { globalSetup } = storeToRefs(globalStore);
  const { currentLocation } = storeToRefs(userStore);
  const { rolesList, userInfo, permissionListMap, permission } = storeToRefs(userRole);
  const { getUserList } = calendar;
  const { locationList } = storeToRefs(location);
  const {listArea, activityList } = storeToRefs(booking);

  const getMyRole = computed(() => {
    return rolesList?.value?.filter((role: any) =>
      userInfo.value?.roles?.includes(role.id)
    );
  });

  const {
    editBookingData,
    usersList,
    recurringDetails,
    isBookingStepperVisible,
  } = storeToRefs(calendar);

  const closeDialog = () => {
    isBookingStepperVisible.value = false
    editBookingData.value = ""
    if(route.name == "pos" && isEditing.value){
      isEditing.value = false
      window.location = window.location
    } else {
      isEditing.value = false
    }
  }

  const userId = ref<any>(pb?.authStore?.model?.id);

  const handleRefetchingData = async (bookingId: string) => {
    try {
      // Fetch the booking record from the database, expanding the 'docketLine' relation.
      const bookingData = await pb.collection("bookings").getOne(bookingId, {
        expand: "docketLine",
      });

      const status = getBookingStatus(
        bookingData?.actualStartTime,
        bookingData?.actualDuration
      );

      editBookingData.value = {
        ...bookingData,
        status,
        startDate: bookingData?.startTime,
        docketId: bookingData?.expand?.docketLine?.docket,
      };
    } catch (e) {
      // Log any errors that occur during the fetch operation.
    }
  };

  const canView = (bookingData:any) => {
    if(permission.value?.bookings === "Create"){
      return true
    }
    if(bookingData.owner == userId.value){
      return true;
    }
    if(bookingData.source === 'Guest Booking'){
      return true
    }

    return false
  }

  const { getUserRolePermission, generatePermission } = userRole;


  const quickMakeBooking = async () => {
    bookingLoading.value = true
    await getUserRolePermission()

    let bookingData: any = {"startDate": moment().toISOString(), "owner": pb.authStore?.model?.id}

    const status = getBookingStatus(
      bookingData?.actualStartTime,
      bookingData?.actualDuration
    );

    editBookingData.value = {
      ...bookingData,
      status,
      startDate: bookingData?.startTime,
      docketId: bookingData?.expand?.docketLine?.docket,
      selectedOption: bookingData.options,
    };

    await getAllAreas();

    window.setTimeout(async () => {
      await getActivityList(userInfo?.value?.membership, bookingData.startTime);
      isBookingStepperVisible.value = true
      bookingLoading.value = false
    }, 500)

  }

  const openPopup = async(bookingId:any) => {
    try{
      isBookingStepperVisible.value = false
      if (bookingId) {
        bookingLoading.value = true
        await getUserRolePermission()
        generatePermission()
        // Wait for permission to be populated if it's still empty (async reactive deps)
        if (isEmpty(permission.value)) {
          await new Promise<void>((resolve) => {
            const unwatch = watch(permission, (val) => {
              if (!isEmpty(val)) {
                unwatch()
                resolve()
              }
            })
            setTimeout(() => { unwatch(); resolve() }, 3000)
          })
        }
        // Get Booking Details
        let bookingData = await pb.collection("bookings").getOne(bookingId, {expand: "docketLine"})

        if(bookingData && bookingData.startTime && canView(bookingData)) {
          const status = getBookingStatus(
            bookingData?.actualStartTime,
            bookingData?.actualDuration
          );

          editBookingData.value = {
            ...bookingData,
            status,
            startDate: bookingData?.startTime,
            docketId: bookingData?.expand?.docketLine?.docket,
            selectedOptions: bookingData.options,
          };

          await getAllAreas();

          window.setTimeout(async() => {
            await getActivityList(userInfo?.value?.membership, bookingData.startTime);
            isBookingStepperVisible.value = true
            bookingLoading.value = false
          }, 500)
        } else if(bookingData && bookingData.startTime && !canView(bookingData) && !pb.authStore?.model?.id){
          let to = {
            name: route.name,
            path:route.path,
            query:route.query,
            params:route.params
          }
          try {
            let uowt = JSON.stringify(to)
            sessionStorage.setItem("userOnWayTo", uowt);
            await router.push("/login")
          } catch(e){
            console.error(e)
          }
        }
      }
    } catch(e){
      console.error(e)
      bookingLoading.value = false
    }
  }

  const isBookingConfirmDialogVisible = ref(false)
  const confirmedBooking = ref<any>(null)
  const referrers = ref<any[]>([])

  const showBookingConfirmDialog = async (bookingId: string) => {
    try {
      const bookingData = await pb.collection("bookings").getOne(bookingId, { requestKey: null })
      if (bookingData.state === "Confirmed" || bookingData.due == 0) {
        referrers.value = await pb.collection("referrers").getFullList({ requestKey: null })
        confirmedBooking.value = bookingData
        isBookingConfirmDialogVisible.value = true
      }
    } catch (e) {
      console.error("showBookingConfirmDialog error", e)
    }
  }

  const handleReferrerUpdate = async (referrer: any) => {
    isBookingConfirmDialogVisible.value = false
    try {
      if (referrer) {
        await pb.collection("bookings").update(confirmedBooking.value.id, { referrer })
      }
    } catch (e) {
      console.error("handleReferrerUpdate error", e)
    }
    confirmedBooking.value = null
  }

  const acceptTermsAndConditions = async () => {
    const bookingId = bookingToAgreeTermsFor.value
    await pb.collection("bookings").update(bookingId, {
      termsNotAgreedTo: false
    })
    if (userId.value) {
      pb.collection("users").update(userId.value, { acceptedTCs: moment().toISOString() }).catch(console.error)
    }
    showTermsConditionsDialog.value = false
    bookingToAgreeTermsFor.value = null
    await openPopup(bookingId)
  }

    const declineTermsAndConditions = async () => {
      await pb.collection("bookings").update(bookingToAgreeTermsFor.value, {
        state: 'Deleted',
        deletedReason: 'Terms and Conditions Declined'
      })
      showTermsConditionsDialog.value = false
      bookingToAgreeTermsFor.value = null
    }

  watch(route, (to) => {
    let params = new URLSearchParams(document.location.search);
    let bookingId = params.get("booking")

    if(params.get("terms")){
      showTermsConditionsDialog.value = true
      bookingToAgreeTermsFor.value = bookingId;
    } else {
      openPopup(bookingId)
    }

  }, {immediate: true, deep: true})

  return {
    isBookingStepperVisible,
    showTermsConditionsDialog,
    editBookingData,
    recurringDetails,
    activityList,
    listArea,
    usersList,
    locationList,
    currentLocation,
    getMyRole,
    userInfo,
    userId,
    permissionListMap,
    globalSetup,
    bookingLoading,
    isEditing,
    isBookingConfirmDialogVisible,
    confirmedBooking,
    referrers,
    openPopup,
    getUserList,
    closeDialog,
    handleRefetchingData,
    quickMakeBooking,
    showBookingConfirmDialog,
    handleReferrerUpdate,
    acceptTermsAndConditions,
    declineTermsAndConditions
  }
})
